import { useState, useEffect } from 'react';

interface MapDirectionsProps {
  storeName: string;
  storeAddress: string;
  lat: number;
  lng: number;
  onClose?: () => void;
}

export default function MapDirections({
  storeName,
  storeAddress,
  lat,
  lng,
  onClose,
}: MapDirectionsProps) {
  const [currentLocation, setCurrentLocation] = useState<{ lat: number; lng: number } | null>(null);
  const [distance, setDistance] = useState<string | null>(null);
  const [duration, setDuration] = useState<string | null>(null);
  const [isLoadingLocation, setIsLoadingLocation] = useState(true);

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  // Get current location
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const loc = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCurrentLocation(loc);
          setIsLoadingLocation(false);

          // Fetch distance/duration from Distance Matrix API
          if (apiKey) {
            fetchDistanceMatrix(loc.lat, loc.lng, lat, lng);
          }
        },
        () => {
          setIsLoadingLocation(false);
        },
        { enableHighAccuracy: true }
      );
    } else {
      setIsLoadingLocation(false);
    }
  }, [lat, lng, apiKey]);

  const fetchDistanceMatrix = async (
    originLat: number,
    originLng: number,
    destLat: number,
    destLng: number
  ) => {
    try {
      // Note: In production, this should go through your backend to protect the API key
      const response = await fetch(
        `https://maps.googleapis.com/maps/api/distancematrix/json?origins=${originLat},${originLng}&destinations=${destLat},${destLng}&key=${apiKey}`
      );
      const data = await response.json();

      if (data.rows?.[0]?.elements?.[0]?.status === 'OK') {
        setDistance(data.rows[0].elements[0].distance.text);
        setDuration(data.rows[0].elements[0].duration.text);
      }
    } catch {
      // Silently fail - distance info is optional
    }
  };

  // Open directions in native maps app or Google Maps
  const openDirections = (app: 'google' | 'apple' | 'waze' | 'native') => {
    const destination = `${lat},${lng}`;
    const destinationEncoded = encodeURIComponent(storeAddress);

    let url = '';

    switch (app) {
      case 'google':
        url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&destination_place_id=&travelmode=driving`;
        break;
      case 'apple':
        url = `maps://maps.apple.com/?daddr=${destinationEncoded}&dirflg=d`;
        break;
      case 'waze':
        url = `https://waze.com/ul?ll=${destination}&navigate=yes`;
        break;
      case 'native':
      default:
        // Try to detect platform and use appropriate maps
        const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
        if (isIOS) {
          url = `maps://maps.apple.com/?daddr=${destinationEncoded}&dirflg=d`;
        } else {
          url = `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`;
        }
        break;
    }

    window.open(url, '_blank');
  };

  // Check if coordinates are valid (not 0,0 or missing)
  const hasValidCoordinates = lat !== 0 && lng !== 0 && Math.abs(lat) <= 90 && Math.abs(lng) <= 180;

  // Generate static map URL
  const getStaticMapUrl = () => {
    if (!apiKey || !hasValidCoordinates) return null;

    const markers = currentLocation
      ? `markers=color:blue%7Clabel:Y%7C${currentLocation.lat},${currentLocation.lng}&markers=color:red%7Clabel:S%7C${lat},${lng}`
      : `markers=color:red%7Clabel:S%7C${lat},${lng}`;

    const path = currentLocation
      ? `&path=color:0x4285F4%7Cweight:4%7C${currentLocation.lat},${currentLocation.lng}%7C${lat},${lng}`
      : '';

    return `https://maps.googleapis.com/maps/api/staticmap?size=400x200&scale=2&maptype=roadmap&${markers}${path}&key=${apiKey}`;
  };

  const staticMapUrl = getStaticMapUrl();

  return (
    <div className="rounded-xl bg-white shadow-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-slate-100">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary-100">
            <svg className="h-5 w-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          </div>
          <div>
            <h3 className="font-medium text-slate-800">{storeName}</h3>
            <p className="text-sm text-slate-500">{storeAddress}</p>
          </div>
        </div>
        {onClose && (
          <button
            onClick={onClose}
            className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-slate-100"
          >
            <svg className="h-5 w-5 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Map Preview */}
      {staticMapUrl ? (
        <div className="relative">
          <img
            src={staticMapUrl}
            alt={`Map showing ${storeName}`}
            className="w-full h-48 object-cover"
          />
          {/* Distance/Duration Overlay */}
          {(distance || duration) && (
            <div className="absolute bottom-2 left-2 right-2 flex gap-2">
              {distance && (
                <div className="rounded-full bg-white/95 px-3 py-1 text-sm font-medium text-slate-700 shadow-sm">
                  {distance}
                </div>
              )}
              {duration && (
                <div className="rounded-full bg-primary-600 px-3 py-1 text-sm font-medium text-white shadow-sm">
                  {duration}
                </div>
              )}
            </div>
          )}
          {isLoadingLocation && (
            <div className="absolute top-2 right-2 rounded-full bg-white/95 px-3 py-1 text-xs text-slate-500 shadow-sm">
              Getting location...
            </div>
          )}
        </div>
      ) : (
        <div className="h-32 bg-gradient-to-br from-slate-100 to-slate-200 flex flex-col items-center justify-center gap-2">
          <svg className="h-8 w-8 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          <p className="text-sm text-slate-500">
            {!hasValidCoordinates ? 'Store coordinates not available' : 'Map preview not available'}
          </p>
        </div>
      )}

      {/* Quick Actions */}
      <div className="p-4">
        {/* Main Navigate Button */}
        <button
          onClick={() => hasValidCoordinates && openDirections('native')}
          disabled={!hasValidCoordinates}
          className={`w-full flex items-center justify-center gap-2 rounded-xl py-4 text-base font-semibold shadow-sm transition-all ${
            hasValidCoordinates
              ? 'bg-primary-600 text-white hover:bg-primary-700 active:scale-[0.98]'
              : 'bg-slate-200 text-slate-400 cursor-not-allowed'
          }`}
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
          </svg>
          {hasValidCoordinates ? 'Start Navigation' : 'No coordinates available'}
        </button>

        {/* Alternative Map Apps */}
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => hasValidCoordinates && openDirections('google')}
            disabled={!hasValidCoordinates}
            className={`flex-1 flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors ${
              hasValidCoordinates
                ? 'border-slate-200 text-slate-700 hover:bg-slate-50'
                : 'border-slate-100 text-slate-300 cursor-not-allowed'
            }`}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z"/>
            </svg>
            Google Maps
          </button>
          <button
            onClick={() => hasValidCoordinates && openDirections('waze')}
            disabled={!hasValidCoordinates}
            className={`flex-1 flex items-center justify-center gap-2 rounded-lg border py-2.5 text-sm font-medium transition-colors ${
              hasValidCoordinates
                ? 'border-slate-200 text-slate-700 hover:bg-slate-50'
                : 'border-slate-100 text-slate-300 cursor-not-allowed'
            }`}
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.54 6.63c-.69-.52-1.56-.78-2.39-.78-.82 0-1.68.26-2.38.78-.7.52-1.3 1.27-1.63 2.17-.33.9-.49 1.93-.49 2.98 0 1.05.16 2.08.49 2.98.33.9.93 1.65 1.63 2.17.7.52 1.56.78 2.38.78.83 0 1.7-.26 2.39-.78.7-.52 1.3-1.27 1.63-2.17.33-.9.49-1.93.49-2.98 0-1.05-.16-2.08-.49-2.98-.33-.9-.93-1.65-1.63-2.17zM12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2z"/>
            </svg>
            Waze
          </button>
        </div>

        {/* Store Info */}
        <div className="mt-4 pt-4 border-t border-slate-100">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <svg className="h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span>{storeAddress}</span>
          </div>
          <button
            onClick={() => navigator.clipboard.writeText(storeAddress)}
            className="mt-2 text-xs text-primary-600 hover:text-primary-700"
          >
            Copy address
          </button>
        </div>
      </div>
    </div>
  );
}
